import { useEffect, useState } from "react";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PrimaryButton, SecondaryButton, Input, Select, Toggle } from "../components/ui-bits";
import { TrashIcon, PlusIcon, UsersThreeIcon, CopyIcon, KeyIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", role: "viewer", auto_puk: true, password: "" });
  const [lastPuk, setLastPuk] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/users");
      setUsers(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onAdd = async (e) => {
    e.preventDefault();
    const body = { email: form.email, name: form.name, role: form.role };
    if (!form.auto_puk && form.password) body.password = form.password;
    try {
      const r = await api.post("/auth/users", body);
      if (r.data.puk) {
        setLastPuk({ email: r.data.email, puk: r.data.puk });
      } else {
        toast.success("User created");
      }
      setForm({ email: "", name: "", role: "viewer", auto_puk: true, password: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const onPatch = async (u, patch) => {
    try {
      await api.patch(`/auth/users/${u.id}`, patch);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const isAdmin = !me || me.role === "admin";

  return (
    <Shell
      title="Users & access"
      subtitle="Corporate mode user management. Admins edit everything, viewers see the feed and their own notification settings."
    >
      {isAdmin && (
        <>
          {lastPuk && (
            <div data-testid="puk-banner" className="border-2 border-zinc-950 bg-zinc-50 p-5 mb-6">
              <div className="flex items-start gap-3">
                <KeyIcon size={22} weight="bold" className="flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-base">One-time PUK for {lastPuk.email}</div>
                  <p className="text-xs text-zinc-600 mt-1 mb-3">
                    Copy this now — it won't be shown again. Send it securely to the user (e.g. SMS).
                    The user must change it on first login.
                  </p>
                  <div className="flex items-center gap-2">
                    <code data-testid="puk-value" className="font-mono-num text-lg bg-zinc-950 text-white px-4 py-2 tracking-[0.2em] select-all flex-1">
                      {lastPuk.puk}
                    </code>
                    <SecondaryButton
                      data-testid="puk-copy"
                      onClick={() => { navigator.clipboard.writeText(lastPuk.puk); toast.success("Copied"); }}
                      className="!px-3 !py-2 flex items-center gap-1"
                    >
                      <CopyIcon size={14} /> Copy
                    </SecondaryButton>
                    <SecondaryButton data-testid="puk-dismiss" onClick={() => setLastPuk(null)}>Done</SecondaryButton>
                  </div>
                </div>
              </div>
            </div>
          )}
          <form onSubmit={onAdd} className="border border-zinc-200 bg-white p-6 mb-8 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-4">
              <div className="overline mb-1.5">Email</div>
              <Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="w-full" />
            </div>
            <div className="md:col-span-3">
              <div className="overline mb-1.5">Name</div>
              <Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full" />
            </div>
            <div className="md:col-span-2">
              <div className="overline mb-1.5">Role</div>
              <Select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full">
                <option value="viewer">Viewer</option>
                <option value="admin">Admin</option>
              </Select>
            </div>
            <div className="md:col-span-3">
              <PrimaryButton type="submit" data-testid="user-add-btn" className="w-full flex items-center justify-center gap-2">
                <PlusIcon size={16} weight="bold" /> Invite (auto-generate PUK)
              </PrimaryButton>
            </div>
            <div className="md:col-span-12 flex items-center gap-3">
              <Toggle
                testId="user-auto-puk"
                checked={form.auto_puk}
                onChange={(v) => setForm({ ...form, auto_puk: v, password: "" })}
                label="Auto-generate one-time PUK (recommended)"
              />
              {!form.auto_puk && (
                <Input data-testid="user-password" type="password" placeholder="Set initial password (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} minLength={8} className="flex-1 max-w-sm" />
              )}
            </div>
          </form>
        </>
      )}

      <div className="border border-zinc-200 bg-white">
        <div className="hidden md:grid grid-cols-12 px-6 py-3 bg-zinc-50 border-b border-zinc-200 overline">
          <div className="col-span-4">User</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">Auth</div>
          <div className="col-span-2">Pushover key</div>
          <div className="col-span-2 text-right">Active</div>
        </div>
        {loading && <div className="p-10 text-center text-sm text-zinc-400">Loading…</div>}
        {!loading && users.length === 0 && (
          <div className="p-12 text-center">
            <UsersThreeIcon size={28} className="mx-auto text-zinc-300 mb-3" />
            <p className="text-sm text-zinc-500">No users yet — invite your team above.</p>
          </div>
        )}
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="grid grid-cols-2 md:grid-cols-12 px-4 sm:px-6 py-3 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 items-center gap-3 text-sm">
            <div className="col-span-2 md:col-span-4 min-w-0">
              <div className="font-medium truncate">{u.email}</div>
              <div className="text-xs text-zinc-500 truncate">{u.name || "—"}</div>
            </div>
            <div className="md:col-span-2">
              {isAdmin && me?.id !== u.id ? (
                <Select value={u.role} onChange={(e) => onPatch(u, { role: e.target.value })}>
                  <option value="viewer">viewer</option>
                  <option value="admin">admin</option>
                </Select>
              ) : (
                <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold bg-zinc-200 text-zinc-700">{u.role}</span>
              )}
            </div>
            <div className="md:col-span-2 text-xs text-zinc-600 uppercase">{u.auth_method}</div>
            <div className="md:col-span-2">
              <Input
                data-testid={`user-pushover-${u.id}`}
                value={u.pushover_user_key || ""}
                onChange={(e) => onPatch(u, { pushover_user_key: e.target.value })}
                placeholder="u…"
                className="w-full font-mono-num text-xs"
                disabled={!isAdmin && me?.id !== u.id}
              />
            </div>
            <div className="md:col-span-2 flex items-center justify-end gap-2">
              <Toggle checked={u.active} onChange={(v) => onPatch(u, { active: v })} testId={`user-active-${u.id}`} />
              {isAdmin && me?.id !== u.id && (
                <SecondaryButton onClick={() => onDelete(u)} className="!px-2 !py-1.5" data-testid={`user-delete-${u.id}`}>
                  <TrashIcon size={14} />
                </SecondaryButton>
              )}
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );
}
